import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '@/config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import ProcessingStatusDisplay from '@/components/sb-search-term-report/ProcessingStatusDisplay';
import { checkMultipleProfileStatuses, ProfileStatus } from '@/services/sbSearchTermService';

interface AmazonProfile {
  profileId: string;
  name?: string;
  displayName?: string;
  accountInfo?: {
    id: string;
    type: string;
    name: string;
  };
  countryCode: string;
  currencyCode: string;
  marketplaceStringId?: string;
  type: 'vendor' | 'seller';
  canRequestReports?: boolean;
}

interface ReportStatus {
  pending: boolean;
  reportId: string | null;
  message: string;
  error: string | null;
  profileId?: string;
}

const ProfileSelection: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState<AmazonProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [generatingReports, setGeneratingReports] = useState<Set<string>>(new Set());
  const [reportStatuses, setReportStatuses] = useState<Map<string, ReportStatus>>(new Map());
  const [profileStatuses, setProfileStatuses] = useState<Map<string, ProfileStatus>>(new Map());
  const [showProcessingStatus, setShowProcessingStatus] = useState(false);
  const [processingCompany, setProcessingCompany] = useState<string>('');

  useEffect(() => {
    loadProfilesAndCompanyInfo();
  }, []);


  const loadProfilesAndCompanyInfo = async () => {
    try {
      // Get company name from session storage or URL params
      const storedUserInfo = sessionStorage.getItem('amazonAuth_userInfo');
      let company = '';
      if (storedUserInfo) {
        const userInfo = JSON.parse(storedUserInfo);
        company = userInfo.companyName;
        setCompanyName(company);
      }

      // Load Amazon profiles
      const getAmazonProfiles = httpsCallable(functions, 'getAmazonProfiles');
      const result = await getAmazonProfiles();
      const data = result.data as any;
      
      if (data.connected && data.profiles?.length > 0) {
        // Filter to only US profiles and apply proper name formatting
        const usProfiles = data.profiles
          .filter((profile: any) => profile.countryCode === 'US')
          .map((profile: any) => ({
            profileId: profile.profileId,
            name: profile.name,
            displayName: profile.displayName || profile.accountInfo?.name || `Profile ${profile.profileId}`,
            accountInfo: profile.accountInfo,
            countryCode: profile.countryCode,
            currencyCode: profile.currencyCode,
            marketplaceStringId: profile.marketplaceStringId,
            type: profile.type,
            canRequestReports: true
          }));
        
        setProfiles(usProfiles);
        
        // Check status for all profiles
        if (company && usProfiles.length > 0) {
          const profileIds = usProfiles.map((p: AmazonProfile) => p.profileId);
          const statuses = await checkMultipleProfileStatuses(company, profileIds);
          setProfileStatuses(statuses);
          
          // Check if any profile is currently processing
          const processingProfile = Array.from(statuses.entries()).find(
            ([_, status]) => status.isProcessing
          );
          
          if (processingProfile) {
            // If a profile is processing, show the processing status display
            setProcessingCompany(company);
            setShowProcessingStatus(true);
          }
        }
      } else {
        toast.error('No Amazon profiles found. Please try reconnecting.');
        navigate('/sb-search-term-report');
      }
    } catch (error: any) {
      console.error('Error loading profiles:', error);
      toast.error('Failed to load Amazon profiles');
      navigate('/sb-search-term-report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async (profileId: string) => {
    setGeneratingReports(prev => new Set(prev).add(profileId));
    
    // Initialize status for this profile
    setReportStatuses(prev => new Map(prev).set(profileId, {
      pending: true,
      reportId: null,
      message: 'Initializing report generation...',
      error: null,
      profileId
    }));
    
    try {
      const generateSBReport = httpsCallable(functions, 'generateSBReport');
      const result = await generateSBReport({
        profileId: profileId,
        companyName: companyName
      });

      const data = result.data as { success: boolean; message?: string; reportId?: string };
      
      if (data.success) {
        toast.success(`Report generation started for ${companyName}!`);
        
        // Show real-time processing status display
        setProcessingCompany(companyName);
        setShowProcessingStatus(true);
        
      } else {
        setReportStatuses(prev => new Map(prev).set(profileId, {
          pending: false,
          reportId: null,
          message: '',
          error: data.message || 'Failed to generate report',
          profileId
        }));
        toast.error(data.message || 'Failed to generate report');
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      setReportStatuses(prev => new Map(prev).set(profileId, {
        pending: false,
        reportId: null,
        message: '',
        error: error.message || 'Failed to generate report',
        profileId
      }));
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setGeneratingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });
    }
  };

  const handleProcessingComplete = () => {
    toast.success('All reports processed successfully! Redirecting to dashboard...');
    setTimeout(() => {
      navigate('/sb-search-term-report');
    }, 2000);
  };

  const handleBackToDashboard = () => {
    // Clear session storage
    sessionStorage.removeItem('amazonAuth_userInfo');
    navigate('/sb-search-term-report');
  };

  const getAccountTypeBadgeColor = (type: string) => {
    return type === 'vendor' 
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  if (isLoading) {
    return (
      <div className="w-full flex flex-col items-center bg-[#111827] text-white min-h-screen p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-400" />
            <p className="text-gray-300">Loading Amazon profiles...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show processing status if reports are generating
  if (showProcessingStatus && processingCompany) {
    return (
      <div className="w-full flex flex-col items-center bg-[#111827] text-white min-h-screen p-6">
        <div className="w-full max-w-4xl">
          <Button
            variant="ghost"
            onClick={handleBackToDashboard}
            className="mb-4 text-gray-300 hover:text-white hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <ProcessingStatusDisplay
            companyName={processingCompany}
            onComplete={handleProcessingComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center bg-[#111827] text-white min-h-screen p-6">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={handleBackToDashboard}
            className="mb-4 text-gray-300 hover:text-white hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              Select Amazon Profile
            </h1>
            <p className="text-xl text-gray-300">
              Choose the profile for <span className="text-blue-400 font-semibold">{companyName}</span>
            </p>
          </div>
        </div>

        {/* Content */}
        {profiles.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-800 rounded-lg p-8 max-w-md mx-auto">
              <h3 className="text-xl font-semibold mb-2 text-white">No US Profiles Found</h3>
              <p className="text-gray-300 mb-4">
                No US Amazon Advertising profiles were found for this account.
              </p>
              <p className="text-sm text-gray-400">
                Only US profiles are supported for SB Search Term reports.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {profiles.map((profile) => {
              const status = reportStatuses.get(profile.profileId);
              const profileStatus = profileStatuses.get(profile.profileId);
              const isGenerating = generatingReports.has(profile.profileId);
              const isSetup = profileStatus?.isSetup || false;
              const isProcessing = profileStatus?.isProcessing || false;
              const canGenerate = profileStatus?.canGenerate ?? true;
              
              return (
                <Card
                  key={profile.profileId}
                  className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-all"
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white">
                        <div className="text-xl font-semibold">{profile.displayName}</div>
                        <p className="text-sm text-gray-400 font-normal mt-1">
                          Profile ID: {profile.profileId}
                        </p>
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <Badge className={getAccountTypeBadgeColor(profile.type)}>
                          {profile.type === 'vendor' ? 'Vendor' : 'Seller'}
                        </Badge>
                        
                        {isProcessing ? (
                          <div className="flex items-center text-blue-400">
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </div>
                        ) : isSetup ? (
                          <div className="flex items-center text-green-400">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Active
                          </div>
                        ) : status?.error ? (
                          <div className="flex items-center text-red-400">
                            <AlertCircle className="w-4 h-4 mr-2" />
                            Failed
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleGenerateReport(profile.profileId)}
                            disabled={isGenerating || !canGenerate}
                            className={canGenerate ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-600 text-gray-400 cursor-not-allowed"}
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : canGenerate ? (
                              'Generate Report'
                            ) : (
                              'Already Set Up'
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Country:</span>
                        <span className="ml-2 font-medium text-white">{profile.countryCode}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Currency:</span>
                        <span className="ml-2 font-medium text-white">{profile.currencyCode}</span>
                      </div>
                      {profile.accountInfo && (
                        <>
                          <div>
                            <span className="text-gray-400">Account:</span>
                            <span className="ml-2 font-medium text-white">{profile.accountInfo.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Account ID:</span>
                            <span className="ml-2 font-medium text-white">{profile.accountInfo.id}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Status Messages */}
                    {(profileStatus?.statusMessage || status?.error) && (
                      <div className="mt-4 p-3 rounded-lg bg-gray-700">
                        {isProcessing ? (
                          <div className="flex items-start text-blue-300">
                            <Loader2 className="w-4 h-4 mr-2 mt-0.5 animate-spin" />
                            <div>
                              <p className="font-medium">Reports Generating</p>
                              <p className="text-sm text-gray-400">{profileStatus?.statusMessage}</p>
                            </div>
                          </div>
                        ) : status?.error ? (
                          <div className="flex items-start text-red-300">
                            <AlertCircle className="w-4 h-4 mr-2 mt-0.5" />
                            <div>
                              <p className="font-medium">Setup Failed</p>
                              <p className="text-sm text-gray-400">{status.error}</p>
                            </div>
                          </div>
                        ) : isSetup && profileStatus?.statusMessage ? (
                          <div className="flex items-start text-green-300">
                            <Clock className="w-4 h-4 mr-2 mt-0.5" />
                            <div>
                              <p className="font-medium">Daily Sync Active</p>
                              <p className="text-sm text-gray-400">{profileStatus.statusMessage}</p>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSelection;